import { useCallback, useMemo, useState } from "react";

import { Button } from "@/components/ui/buttons/button";
import { useBackAction } from "@/hooks/use-back-action";
import { useSPAContext } from "@/providers/spa-provider";
import { RouteNames } from "@/types/route-names";

export type DojoFormat = "1v1" | "3v3";
export type DojoCpuType =
  | "random"
  | "clone"
  | "neutral"
  | "advantage"
  | "disadvantage"
  | "custom";

export type DojoTrainingConfig = {
  format: DojoFormat;
  cpuType: DojoCpuType;
};

const STORAGE_KEY = "dojo_training_config_v1";

export function readDojoTrainingConfig(): DojoTrainingConfig {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return { format: "3v3", cpuType: "clone" };
    }
    const parsed = JSON.parse(raw) as Partial<DojoTrainingConfig>;
    const format = parsed.format === "1v1" ? "1v1" : "3v3";
    const cpuType: DojoCpuType =
      parsed.cpuType === "random" ||
      parsed.cpuType === "neutral" ||
      parsed.cpuType === "advantage" ||
      parsed.cpuType === "disadvantage" ||
      parsed.cpuType === "custom" ||
      parsed.cpuType === "clone"
        ? parsed.cpuType
        : "clone";
    return { format, cpuType };
  } catch {
    return { format: "3v3", cpuType: "clone" };
  }
}

export function writeDojoTrainingConfig(config: DojoTrainingConfig) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
}

export const DojoTrainingConfigView = () => {
  const { setCurrentRoute, previousRoute } = useSPAContext();

  const [format, setFormat] = useState<DojoFormat>(() => {
    const cfg = readDojoTrainingConfig();
    return cfg.format;
  });
  const [cpuType, setCpuType] = useState<DojoCpuType>(() => {
    const cfg = readDojoTrainingConfig();
    return cfg.cpuType;
  });

  const handleBack = useCallback(() => {
    setCurrentRoute(previousRoute ?? RouteNames.DOJO);
  }, [previousRoute, setCurrentRoute]);

  useBackAction(handleBack);

  const picksRequired = useMemo(() => (format === "1v1" ? 1 : 3), [format]);

  const handleContinue = useCallback(() => {
    writeDojoTrainingConfig({ format, cpuType });
    setCurrentRoute(RouteNames.DOJO_TEAM_SELECT);
  }, [cpuType, format, setCurrentRoute]);

  return (
    <div className="relative flex flex-col bg-pk-night-1/90 backdrop-blur-md pb-24 w-full h-full overflow-y-auto">
      {/* Header */}
      <div className="z-10 relative flex justify-between items-center mt-6 xs:mt-8">
        <div className="flex flex-col ml-4 p-2">
          <div className="relative">
            <h1 className="drop-shadow-lg font-black text-white text-3xl xs:text-4xl uppercase leading-none tracking-tight">
              TRAINING
            </h1>
            <div className="bg-pk-red-1 mt-1 w-12 xs:w-16 h-1 skew-x-[-20deg]" />
          </div>
          <p className="mt-2 text-pk-gray-4 text-sm">
            Configure your match ({picksRequired} pick
            {picksRequired > 1 ? "s" : ""})
          </p>
        </div>
        <button
          onClick={handleBack}
          className="mr-4 px-4 py-2 text-pk-gray-4 hover:text-white transition-colors"
        >
          ← Back
        </button>
      </div>

      <div className="z-10 relative flex flex-col gap-6 mx-auto px-4 pt-6 w-full max-w-md">
        {/* Format */}
        <div className="bg-black/20 p-4 border border-white/10 rounded-xl">
          <div className="mb-3 font-bold text-pk-gray-4 text-xs uppercase tracking-widest">
            Format
          </div>
          <div className="flex gap-3">
            <Button
              variant={format === "1v1" ? "red" : "night"}
              className="flex-1"
              onClick={() => setFormat("1v1")}
            >
              1v1
            </Button>
            <Button
              variant={format === "3v3" ? "red" : "night"}
              className="flex-1"
              onClick={() => setFormat("3v3")}
            >
              3v3
            </Button>
          </div>
        </div>

        {/* CPU Type */}
        <div className="bg-black/20 p-4 border border-white/10 rounded-xl">
          <div className="mb-3 font-bold text-pk-gray-4 text-xs uppercase tracking-widest">
            CPU Type
          </div>
          <div className="gap-3 grid grid-cols-2">
            <Button
              variant={cpuType === "random" ? "yellow" : "night"}
              onClick={() => setCpuType("random")}
            >
              Random
            </Button>
            <Button
              variant={cpuType === "clone" ? "yellow" : "night"}
              onClick={() => setCpuType("clone")}
            >
              Clone
            </Button>
            <Button
              variant={cpuType === "advantage" ? "yellow" : "night"}
              onClick={() => setCpuType("advantage")}
            >
              Advantage
            </Button>
            <Button
              variant={cpuType === "disadvantage" ? "yellow" : "night"}
              onClick={() => setCpuType("disadvantage")}
            >
              Disadvantage
            </Button>
            <Button
              variant={cpuType === "neutral" ? "yellow" : "night"}
              onClick={() => setCpuType("neutral")}
            >
              Neutral
            </Button>
            <Button
              variant={cpuType === "custom" ? "yellow" : "night"}
              onClick={() => setCpuType("custom")}
            >
              Custom
            </Button>
          </div>
          {cpuType === "custom" && (
            <p className="mt-3 text-pk-gray-4 text-xs">
              Custom will let you pick both Pokémon and skills for both sides.
            </p>
          )}
        </div>

        <Button variant="red" className="py-4 w-full" onClick={handleContinue}>
          <span className="font-black tracking-wider">CONTINUE</span>
        </Button>
      </div>
    </div>
  );
};

